import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Clock, Save } from 'lucide-react';

const DAYS = [
    { key: 'monday', label: 'Luni' },
    { key: 'tuesday', label: 'Marți' },
    { key: 'wednesday', label: 'Miercuri' },
    { key: 'thursday', label: 'Joi' },
    { key: 'friday', label: 'Vineri' },
    { key: 'saturday', label: 'Sâmbătă' },
    { key: 'sunday', label: 'Duminică' },
];

const DEFAULT_SCHEDULE = Object.fromEntries(
    DAYS.map(({ key }) => [key, { closed: key === 'sunday', open: '09:00', close: '23:00' }])
);

const emptyHours = () => ({
    enabled: false,
    timezone: 'Europe/Bucharest',
    schedule: Object.fromEntries(
        Object.entries(DEFAULT_SCHEDULE).map(([k, v]) => [k, { ...v }])
    ),
});

export function OpeningHoursSettings() {
    const { refresh } = useAuth();
    const [hours, setHours] = useState(emptyHours());
    const [liveStatus, setLiveStatus] = useState(null);
    const [summary, setSummary] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get('/r/restaurant/settings')
            .then(({ data }) => {
                setHours(data.opening_hours || emptyHours());
                setLiveStatus(data.open_status || null);
                setSummary(data.schedule_summary || '');
            })
            .catch(() => setError('Nu am putut încărca programul.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const setDay = (key, patch) => {
        setHours(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [key]: { ...prev.schedule[key], ...patch },
            },
        }));
    };

    const save = async () => {
        setError(''); setSaved(''); setBusy(true);
        try {
            const { data } = await api.patch('/r/restaurant/settings', { opening_hours: hours });
            setHours(data.opening_hours);
            setLiveStatus(data.open_status);
            setSummary(data.schedule_summary || '');
            setSaved('Program salvat.');
            await refresh({ silent: true });
        } catch (e) {
            setError(formatApiError(e));
        } finally { setBusy(false); }
    };

    if (loading) {
        return (
            <div className="panel" style={{ maxWidth: 640, marginBottom: '1.4rem' }}>
                <div className="spinner" style={{ margin: '0.5rem auto' }} />
            </div>
        );
    }

    return (
        <div className="panel" style={{ maxWidth: 640, marginBottom: '1.4rem' }} data-testid="opening-hours-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.6rem' }}>
                <Clock size={18} color="var(--amber)" />
                <h2 className="headline" style={{ fontSize: '1.35rem', margin: 0 }}>Program de funcționare</h2>
                <span className={`badge badge-${liveStatus?.is_open === false ? 'muted' : 'teal'}`}>
                    {hours.enabled
                        ? (liveStatus?.is_open ? 'Deschis acum' : 'Închis acum')
                        : 'QR permanent activ'}
                </span>
            </div>

            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0, lineHeight: 1.5 }}>
                Codurile QR permit comenzi doar în intervalul setat. În afara orelor, clienții văd meniul dar nu pot comanda.
            </p>

            {liveStatus?.today_label && hours.enabled && (
                <div style={{ fontSize: '0.85rem', color: 'var(--neon-cyan)', marginBottom: '0.8rem' }}>
                    Azi ({liveStatus.today_label}): {liveStatus.today_hours}
                    {liveStatus.message && !liveStatus.is_open && (
                        <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>{liveStatus.message}</div>
                    )}
                </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', cursor: 'pointer' }}>
                <input
                    type="checkbox"
                    checked={hours.enabled}
                    onChange={e => setHours(prev => ({ ...prev, enabled: e.target.checked }))}
                    data-testid="hours-enabled"
                />
                <span>Activează restricția QR după program</span>
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '1rem' }}>
                {DAYS.map(({ key, label }) => {
                    const day = hours.schedule?.[key] || { closed: false, open: '09:00', close: '23:00' };
                    return (
                        <div key={key} style={{ display: 'grid', gridTemplateColumns: '88px 1fr 1fr auto', gap: 8, alignItems: 'center' }}
                             data-testid={`hours-row-${key}`}>
                            <span style={{ color: '#fff', fontSize: '0.88rem' }}>{label}</span>
                            <input className="input" type="time" value={day.open} disabled={day.closed}
                                   onChange={e => setDay(key, { open: e.target.value })} />
                            <input className="input" type="time" value={day.close} disabled={day.closed}
                                   onChange={e => setDay(key, { close: e.target.value })} />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                <input type="checkbox" checked={day.closed} onChange={e => setDay(key, { closed: e.target.checked })} />
                                Închis
                            </label>
                        </div>
                    );
                })}
            </div>

            {summary && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.8rem' }}>{summary}</div>
            )}

            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}
            {saved && <div style={{ color: 'var(--neon-teal)', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{saved}</div>}

            <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy} data-testid="hours-save">
                <Save size={14} /> Salvează programul
            </button>
        </div>
    );
}
